import React, { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  X,
  Plus,
  Shield,
  Clock,
  User,
  Tag,
  CheckCircle,
  AlertCircle,
  FileText,
  Camera,
  Video,
  Mic,
  Lock,
  Send,
  ArrowLeft,
  Calendar,
  BookOpen,
  Heart,
  Users,
  TrendingUp,
  MoreHorizontal,
  Flag,
  Search,
  Brain,
  Hand,
  GraduationCap,
  Ban,
  RefreshCw,
  Paperclip,
  Pencil,
  MessageCircle,
} from "lucide-react";
import axios from "../axios";
import { format } from "date-fns";
import { IP } from "../internalServer";
import { Grid, Card, CardContent, Typography, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

const Safety = () => {
  const [showModal, setShowModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [concernOptions, setConcernOptions] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [attachmentSize, setAttachmentSize] = useState([]);
  const [caseAttachments, setCaseAttachments] = useState([]);
  const [isValidFileType, setIsValidFileType] = useState(true);
  const [recentReports, setRecentReports] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [caseNumber, setCaseNumber] = useState([]);

  const [selectedCase, setSelectedCase] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [isCaseDetailsModalOpen, setIsCaseDetailsModalOpen] = useState(false);

  let studentData = JSON.parse(localStorage.getItem("breathForStudent"));
  const [studentDetails, setStudentDetails] = useState(studentData);
  const adminUserId = studentDetails?.id;

  const [formData, setFormData] = useState({
    concern: "",
    isEmergency: "",
    description: "",
    keepIdentityConfidential: false,
  });

  const fileInputRef = useRef(null);
  const studentId = studentDetails.id;
  const schoolId = studentDetails.school.id;
  const standardId = studentDetails.standard.id;
  const sectionId = studentDetails.section.id;

  const fetchConcernCategories = async () => {
    try {
      const response = await axios.get(
        `studentSafety/categories/get?schoolId=${schoolId}`,
      );

      // Map API icon names to Lucide icons
      const iconMap = {
        bullying: Users,
        "mental-health": Brain,
        safety: Shield,
        behavior: Hand,
        academic: GraduationCap,
        harassment: AlertTriangle,
        violence: Ban,
        discrimination: AlertTriangle,
        "self-harm": Heart,
      };

      const categoryData = response?.data?.data?.map((item) => ({
        value: item.name,
        id: item.id,
        isHighRisk: item.isHighRisk,
        sortOrder: item.sortOrder,
        icon: iconMap[item.icon] || Users,
        color: item.isHighRisk ? "text-red-500" : "text-blue-500",
      }));

      setConcernOptions(categoryData);
    } catch (error) {
      console.error("Error fetching concern categories:", error);
    }
  };

  const fetchMyCases = async () => {
    try {
      setLoadingCases(true);

      const response = await axios.get(
        `/studentSafety/myCases?userId=${studentId}`,
      );

      const cases = response?.data?.data?.cases || [];

      // Map API response for table
      const iconMap = {
        bullying: Users,
        "mental-health": Brain,
        safety: Shield,
        behavior: Hand,
        academic: GraduationCap,
        harassment: AlertTriangle,
        violence: Ban,
        discrimination: AlertTriangle,
        "self-harm": Heart,
      };

      const formattedCases = cases.map((item) => ({
        id: item.id,
        caseNumber: item.caseNumber,
        category: item.category?.name,
        categoryIcon: iconMap[item.category?.icon] || Users,
        reportedOn: item.createdAt,
        priority: item.priority,
        status: item.status,
        isAnonymous: item.isAnonymous,
        description: item.description,
        emergency: item.isEmergency,
      }));

      setRecentReports(formattedCases);
    } catch (error) {
      console.error("Error fetching cases", error);
    } finally {
      setLoadingCases(false);
    }
  };

  useEffect(() => {
    if (!schoolId) return;

    fetchConcernCategories();
    fetchMyCases();
  }, [schoolId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setShowModal(false);
    setCurrentStep(1);
    setFormData({
      concern: "",
      isEmergency: "",
      description: "",
      keepIdentityConfidential: false,
    });
  };

  const handleNextStep = () => {
    if (currentStep === 1 && formData.concern) {
      setCurrentStep(2);
    } else if (currentStep === 2 && formData.isEmergency) {
      setCurrentStep(3);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Check if Next button should be disabled based on current step
  const isNextDisabled = () => {
    if (currentStep === 1) {
      return !formData.concern;
    }
    if (currentStep === 2) {
      return !formData.isEmergency;
    }
    return false;
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "critical":
        return "bg-red-100 text-red-700 border-red-300";

      case "high":
        return "bg-orange-100 text-orange-700 border-orange-300";

      case "medium":
        return "bg-yellow-100 text-yellow-700 border-yellow-300";

      case "low":
        return "bg-green-100 text-green-700 border-green-300";

      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority?.toLowerCase()) {
      case "critical":
        return <AlertTriangle className="w-3 h-3" />;

      case "high":
        return <AlertCircle className="w-3 h-3" />;

      case "medium":
        return <Clock className="w-3 h-3" />;

      case "low":
        return <CheckCircle className="w-3 h-3" />;

      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "In Progress":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Under Review":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "Resolved":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "In Progress":
        return <Clock className="w-3 h-3" />;
      case "Under Review":
        return <AlertCircle className="w-3 h-3" />;
      case "Resolved":
        return <CheckCircle className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const validateFileType = (file) => {
    const allowedExtensions = /\.(jpg|jpeg|png|webp|mp4|mov|avi|mkv)$/i;

    if (!allowedExtensions.test(file.name) || attachedFiles.length >= 5) {
      setIsValidFileType(false);
      return false;
    }

    setIsValidFileType(true);
    return true;
  };
  const handleAttachment = async (files) => {
    let attachments = [];
    let attachmentSizes = [];
    const totalSize = attachedFiles.reduce(
      (accumulator, current) => accumulator + current.size,
      0,
    );
    console.log(files);
    if (files) {
      for (const file of files) {
        if (
          totalSize + file.size < 10 * 1024 * 1024 &&
          validateFileType(file)
        ) {
          try {
            let formData = new FormData();
            formData.append("name", file.name);
            formData.append("attachment", file);
            let res = await axios.post(
              `/studentSafety/uploadAttachment/userId/${studentId}`,
              formData,
            );
            console.log(res);
            res && setAttachmentSize((prev) => [...prev, file.size]);
            res && setCaseAttachments((prev) => [...prev, res.data.data]);
            res &&
              setAttachedFiles((prev) => {
                attachmentSizes.push(...attachmentSize, file.size);
                return [...prev, file];
              });
          } catch (error) {
            console.log(error);
          }
        } else {
          setIsValidFileType(false);
        }
      }
    }
  };

  const handleFileChange = (e) => {
    handleAttachment(e.target.files);
    e.target.value = null; // Reset input
  };

  const handleSubmit = async () => {
    // Remove e.preventDefault() since we're using type="button"
    // e.preventDefault(); // <- Remove this line

    try {
      setIsSubmitting(true);

      // Find selected category
      const selectedCategory = concernOptions.find(
        (item) => item.value === formData.concern,
      );

      const payload = {
        schoolId,
        studentId,
        standardId,
        sectionId,
        categoryId: selectedCategory?.id,
        description: formData.description,
        isEmergency: formData.isEmergency === "yes",
        isAnonymous: formData.keepIdentityConfidential,
        attachments: caseAttachments,
        attachments_sizeKb: attachmentSize,
      };
      console.log("payload", payload);

      const response = await axios.post("/studentSafety/create", payload);

      if (response?.data?.success) {
        setCaseNumber(response?.data?.data);
        await fetchMyCases();

        // ✅ SHOW SUCCESS MODAL - Uncomment these lines
        setShowSuccess(true);
        setCurrentStep(1);

        setFormData({
          concern: "",
          isEmergency: "",
          description: "",
          keepIdentityConfidential: false,
        });

        setCaseAttachments([]);
        setAttachmentSize([]);
        setAttachedFiles([]);
      }
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.message || "Failed to submit report.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredReports = recentReports.filter((report) => {
    // Priority filter
    if (
      priorityFilter !== "all" &&
      report.priority?.toLowerCase() !== priorityFilter
    ) {
      return false;
    }
    // Status filter
    if (
      statusFilter !== "all" &&
      report.status?.toLowerCase().replace("_", " ") !== statusFilter
    ) {
      return false;
    }
    // Search filter
    if (
      search &&
      !report.caseNumber?.toLowerCase().includes(search.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const paginatedReports = filteredReports.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [priorityFilter, statusFilter, search]);

  // Clear filters function
  const clearFilters = () => {
    setPriorityFilter("all");
    setStatusFilter("all");
    setSearch("");
    setCurrentPage(1);
  };

  const openChat = async (report) => {
    try {
      setSelectedCase(report);
      setChatLoading(true);

      const res = await axios.get(
        `/studentSafety/get/${report.id}?userId=${adminUserId}&userRole=Student`,
      );

      setChatMessages(res.data.data.comments || []);
    } catch (err) {
      console.error(err);
      setChatMessages([]);
    } finally {
      setChatLoading(false);
    }
  };

  const closeChat = () => {
    setSelectedCase(null);
    setChatMessages([]);
    setChatMessage("");
  };
  const sendMessage = async () => {
    if (!chatMessage.trim()) return;

    try {
      setSendingMessage(true);

      await axios.post(`/studentSafety/${selectedCase?.id}/comment/create`, {
        caseId: selectedCase?.id,
        comment: chatMessage,
        visibility: "public",
        userId: adminUserId,
        userRole: "Student",
      });

      setChatMessage("");

      // Refresh chat
      openChat(selectedCase);
    } catch (err) {
      console.error(err);
    } finally {
      setSendingMessage(false);
    }
  };

  const priorityBadge = (priority) => {
    const map = {
      High: "bg-red-100 text-red-700",
      Medium: "bg-yellow-100 text-yellow-700",
      Low: "bg-green-100 text-green-700",
    };
    return map[priority] || " ";
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  };

  useEffect(() => {
    if (selectedCase && chatMessages.length > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [chatMessages, selectedCase]);

  const handleRemoveAttachment = (index) => {
    setCaseAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  console.log("selectedCase", selectedCase);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Header with Report Button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Flag className="w-8 h-8 text-blue-600" />
              Recent Reports
            </h1>
            <p className="text-gray-600 mt-1">Track your reported issues</p>
          </div>
          <div className="flex gap-2">
            <div className="flex flex-col gap-1.5">
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="h-9 w-[150px] rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              >
                <option value="all">All Priority</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-9 w-[150px] rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="assigned">Assigned</option>
                <option value="in progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                placeholder="Search by ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-[200px] rounded-lg border border-gray-200 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <button
              onClick={clearFilters}
              className="h-9 px-4 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 transition-colors duration-200"
            >
              Clear Filters
            </button>
          </div>
          <div className="flex flex-col">
            <button
              onClick={() => setShowModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 transform hover:scale-105"
            >
              <Plus className="w-5 h-5" />
              Report an Issue
            </button>
            <div className="mt-3 text-right text-xs">
              <span className="mr-1 inline-block h-2 w-2 rounded-full bg-blue-500"></span>
              Click "You" to open chat
            </div>
          </div>
        </div>

        {/* Reports Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="rounded-xl border border-gray-200">
            <div className="max-h-[350px] orange-scrollBarSummary overflow-y-auto">
              <table className="min-w-full border-separate border-spacing-0">
                <thead className="sticky top-0 z-20 bg-white shadow-sm">
                  <tr>
                    <th className="bg-white px-6 py-3 text-left text-xs font-medium uppercase">
                      Case No.
                    </th>
                    <th className="bg-white px-6 py-3 text-left text-xs font-medium uppercase">
                      Category
                    </th>
                    <th className="bg-white px-6 py-3 text-left text-xs font-medium uppercase">
                      Student
                    </th>
                    <th className="bg-white px-6 py-3 text-left text-xs font-medium uppercase">
                      Class
                    </th>
                    <th className="bg-white px-6 py-3 text-left text-xs font-medium uppercase">
                      Reported On
                    </th>
                    <th className="bg-white px-6 py-3 text-left text-xs font-medium uppercase">
                      Priority
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingCases ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center py-10 text-gray-500"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : paginatedReports.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center py-10 text-gray-500"
                      >
                        No reports found.
                      </td>
                    </tr>
                  ) : (
                    paginatedReports.map((report) => {
                      const Icon = report.categoryIcon;

                      return (
                        <tr
                          key={report.id}
                          onClick={() => {
                            setSelectedCase(report);
                            setIsCaseDetailsModalOpen(true);
                          }}
                          className="hover:bg-gray-50 transition duration-150"
                        >
                          <td className="px-6 py-4 font-medium">
                            {report.caseNumber}
                          </td>

                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-gray-500" />
                              {report.category}
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              {`${format(new Date(report.reportedOn), "dd/MM/yyyy")}, ${format(
                                new Date(report.reportedOn),
                                "h:mm:ss a",
                              )}`}
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(report.priority)}`}
                            >
                              {getPriorityIcon(report.priority)}
                              {report.priority.toUpperCase()}
                            </span>
                          </td>

                          <td className="px-6 py-4">
                            <span
                              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                report.status === "new"
                                  ? "bg-blue-100 text-blue-700"
                                  : report.status === "assigned"
                                    ? "bg-purple-100 text-purple-700"
                                    : report.status === "in_progress"
                                      ? "bg-yellow-100 text-yellow-700"
                                      : report.status === "resolved"
                                        ? "bg-green-100 text-green-700"
                                        : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {report.status.replace("_", " ").toUpperCase()}
                            </span>
                          </td>

                          <td className="px-4 py-3 text-sm">
                            {report.isAnonymous ? (
                              <span className="cursor-not-allowed text-gray-400">
                                Anonymous
                                <span className="block text-xs">
                                  (Chat not allowed)
                                </span>
                              </span>
                            ) : report.status === "resolved" ? (
                              <span className="cursor-not-allowed text-gray-400">
                                Closed
                                <span className="block text-xs">
                                  (Case resolved)
                                </span>
                              </span>
                            ) : (
                              <button
                                onClick={() => openChat(report)}
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                You
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t bg-white px-6 py-4">
              <p className="text-sm text-gray-600">
                Showing {startIndex + 1} -
                {Math.min(endIndex, filteredReports.length)} of{" "}
                {filteredReports.length} reports
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="rounded-md border px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`h-9 w-9 rounded-md text-sm ${
                      currentPage === index + 1
                        ? "bg-blue-600 text-white"
                        : "border hover:bg-gray-100"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}

                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-md border px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && !showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl transform transition-all">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center z-10">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-blue-600" />
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Report an Issue
                    </h2>
                    <p className="text-sm text-gray-500">
                      Step {currentStep} of 3
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition duration-200 p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Progress Bar */}
              <div className="px-6 pt-6">
                <div className="flex items-center gap-2">
                  <div
                    className={`flex-1 h-2 rounded-full transition-all duration-300 ${currentStep >= 1 ? "bg-blue-600" : "bg-gray-200"}`}
                  />
                  <div
                    className={`flex-1 h-2 rounded-full transition-all duration-300 ${currentStep >= 2 ? "bg-blue-600" : "bg-gray-200"}`}
                  />
                  <div
                    className={`flex-1 h-2 rounded-full transition-all duration-300 ${currentStep >= 3 ? "bg-blue-600" : "bg-gray-200"}`}
                  />
                </div>
              </div>

              <form className="p-6 space-y-6">
                {/* Step 1: Concern */}
                {currentStep === 1 && (
                  <div className="space-y-6 animate-in slide-in-from-right-5 duration-300 ">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-blue-600" />
                        What is the concern about?
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {concernOptions.map((option) => {
                          const Icon = option.icon;
                          return (
                            <label
                              key={option.value}
                              className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                                formData.concern === option.value
                                  ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                                  : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                              }`}
                            >
                              <input
                                type="radio"
                                name="concern"
                                value={option.value}
                                checked={formData.concern === option.value}
                                onChange={handleInputChange}
                                className="sr-only"
                                required
                              />
                              <Icon className={`w-5 h-5 ${option.color}`} />
                              <span className="text-gray-700">
                                {option.value}
                              </span>
                              {formData.concern === option.value && (
                                <CheckCircle className="w-5 h-5 text-blue-600 ml-auto" />
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Emergency & Description */}
                {currentStep === 2 && (
                  <div className="space-y-6 animate-in slide-in-from-right-5 duration-300">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        Is this an emergency?
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <label
                          className={`flex items-center justify-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                            formData.isEmergency === "yes"
                              ? "border-red-500 bg-red-50 ring-2 ring-red-200"
                              : "border-gray-200 hover:border-red-300 hover:bg-red-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name="isEmergency"
                            value="yes"
                            checked={formData.isEmergency === "yes"}
                            onChange={handleInputChange}
                            className="sr-only"
                            required
                          />
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                          <span className="font-medium">Yes</span>
                        </label>
                        <label
                          className={`flex items-center justify-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                            formData.isEmergency === "no"
                              ? "border-green-500 bg-green-50 ring-2 ring-green-200"
                              : "border-gray-200 hover:border-green-300 hover:bg-green-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name="isEmergency"
                            value="no"
                            checked={formData.isEmergency === "no"}
                            onChange={handleInputChange}
                            className="sr-only"
                            required
                          />
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <span className="font-medium">No</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" />
                        Please describe what happened (optional)
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="4"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition duration-200"
                        placeholder="I am being teased by some seniors in class and it makes me uncomfortable."
                      />
                    </div>
                  </div>
                )}

                {/* Step 3: Attachments & Confirmation */}
                {currentStep === 3 && (
                  <div className="space-y-6 animate-in slide-in-from-right-5 duration-300">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <Camera className="w-4 h-4 text-blue-600" />
                        Add Attachments (optional)
                      </label>
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".jpg,.jpeg,.png,.mp4,mkv"
                          multiple
                          className="hidden"
                          onChange={handleFileChange}
                        />

                        <div className="flex flex-wrap gap-3">
                          {[{ icon: Paperclip, label: "Upload Files" }].map(
                            ({ icon: Icon, label }) => (
                              <button
                                key={label}
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex items-center gap-2 px-6 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 group"
                              >
                                <Icon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                <span>{label}</span>
                              </button>
                            ),
                          )}
                        </div>
                      </>
                    </div>
                    <div className="flex gap-2">
                      {caseAttachments?.length > 0 ? (
                        caseAttachments.map((attachment, index) => {
                          const extension = attachment
                            .split(".")
                            .pop()
                            ?.toLowerCase();

                          const isImage = [
                            "jpg",
                            "jpeg",
                            "png",
                            "webp",
                          ].includes(extension);
                          const isVideo = ["mp4", "mov", "avi", "mkv"].includes(
                            extension,
                          );

                          // Skip non-image/video files
                          if (!isImage && !isVideo) return null;

                          return (
                            <div key={index} className="relative">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveAttachment(index)}
                                sx={{
                                  position: "absolute",
                                  top: 5,
                                  right: 5,
                                  bgcolor: "rgba(0,0,0,.7)",
                                  color: "#fff",
                                  "&:hover": {
                                    bgcolor: "rgba(0,0,0,.9)",
                                  },
                                  width: 26,
                                  height: 26,
                                  zIndex: 10,
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                              {isImage ? (
                                <img
                                  src={IP + attachment.replace(/^\./, "")}
                                  alt="Attachment"
                                  style={{
                                    width: 100,
                                    height: 100,
                                    objectFit: "cover",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleClick(attachment)}
                                />
                              ) : (
                                <div
                                  className="vidio_player mt-3"
                                  onClick={() => handleClick(attachment)}
                                  style={{
                                    width: 100,
                                    height: 100,
                                    cursor: "pointer",
                                  }}
                                >
                                  <video
                                    src={IP + attachment.replace(/^\./, "")}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: 8,
                                    }}
                                    muted
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <Card
                          sx={{
                            width: "100%",
                            padding: "20px",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="body1" color="text.secondary">
                            No attachments available!!
                          </Typography>
                        </Card>
                      )}
                    </div>

                    <div>
                      <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-blue-300 transition-all duration-200 cursor-pointer">
                        <input
                          type="checkbox"
                          name="keepIdentityConfidential"
                          checked={formData.keepIdentityConfidential}
                          onChange={handleInputChange}
                          className="w-5 h-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition duration-200"
                        />
                        <div className="flex items-center gap-2">
                          <Lock className="w-5 h-5 text-gray-500" />
                          <span className="text-gray-700 font-medium">
                            Keep my identity confidential
                          </span>
                        </div>
                      </label>
                    </div>

                    {/* Review Section */}
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <p className="text-sm font-medium text-gray-700">
                        Review your report:
                      </p>
                      <div className="space-y-1 text-sm">
                        <p>
                          <span className="text-gray-500">Concern:</span>{" "}
                          {formData.concern || "Not specified"}
                        </p>
                        <p>
                          <span className="text-gray-500">Emergency:</span>{" "}
                          {formData.isEmergency === "yes" ? "Yes" : "No"}
                        </p>
                        <p>
                          <span className="text-gray-500">Description:</span>{" "}
                          {formData.description || "Not provided"}
                        </p>
                        <p>
                          <span className="text-gray-500">Confidential:</span>{" "}
                          {formData.keepIdentityConfidential ? "Yes" : "No"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200 flex items-center gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </button>
                  )}

                  {currentStep < 3 ? (
                    <button
                      type="button"
                      onClick={handleNextStep}
                      disabled={isNextDisabled()}
                      className={`flex-1 px-6 py-2.5 rounded-lg font-medium transition duration-200 flex items-center justify-center gap-2 ${
                        isNextDisabled()
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg"
                      }`}
                    >
                      Next
                      <ArrowLeft className="w-4 h-4 rotate-180" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className={`flex-1 px-6 py-2.5 rounded-lg font-medium transition duration-200 flex items-center justify-center gap-2 ${
                        isSubmitting
                          ? "bg-gray-400 text-white cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700 text-white hover:shadow-lg"
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Submit Report
                        </>
                      )}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center shadow-2xl transform transition-all animate-in zoom-in-95 duration-300">
              <div className="mb-6">
                <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Thank you for speaking up!
              </h3>
              <p className="text-gray-600 mb-6">
                Your report has been submitted successfully.
              </p>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border-2 border-blue-200">
                <p className="text-sm text-gray-500 mb-1">Report ID</p>
                <p className="text-2xl font-mono font-bold text-blue-600">
                  {caseNumber.caseNumber}
                </p>
                <div className="mt-3 flex justify-center gap-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                    Submitted
                  </span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                    Received
                  </span>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                <Shield className="w-4 h-4 inline-block mr-1 text-green-600" />
                We take every report seriously.
                <br />
                Our team will review and act on it.
              </p>

              <button
                onClick={handleCloseSuccess}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 hover:shadow-lg flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Reports
              </button>
            </div>
          </div>
        )}
        {selectedCase && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-lg">
            <div className="mx-4 flex max-h-[80vh] w-full max-w-2xl flex-col bg-white rounded-2xl shadow-2xl">
              {/* Chat Header */}
              <div className="flex items-center justify-between border-b border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center rounded-full bg-indigo-100 font-bold text-indigo-700">
                    <img
                      src={
                        studentDetails?.profile_picture
                          ? IP +
                            studentDetails?.profile_picture.replace(/^\./, "")
                          : studentDetails?.gender === "M"
                            ? studentDetails?.avatar_profile
                              ? "/images/Boys/" +
                                studentDetails?.avatar_profile +
                                ".webp"
                              : "/images/Student_boy_pic.png"
                            : studentDetails?.gender === "F"
                              ? studentDetails?.avatar_profile
                                ? "/images/Girls/" +
                                  studentDetails?.avatar_profile +
                                  ".webp"
                                : "/images/Student_girl_pic.png"
                              : "/images/default image.png"
                      }
                      alt="icon"
                      className="w-12 h-12 rounded-full"
                      key={studentDetails?.avatar_profile}
                      onError={(e) => {
                        if (studentDetails?.gender === "M") {
                          e.target.src = studentDetails?.avatar_profile
                            ? "/images/Boys/" +
                              studentDetail?.avatar_profile +
                              ".webp"
                            : "/images/Student_boy_pic.png";
                        } else if (studentDetails?.gender === "F") {
                          e.target.src = studentDetails?.avatar_profile
                            ? "/images/Girls/" +
                              studentDetails?.avatar_profile +
                              ".webp"
                            : "/images/Student_girl_pic.png";
                        } else {
                          e.target.src = "/images/default image.png";
                        }
                      }}
                    />
                  </div>
                  <div>
                    <h5 className="font-semibold">{studentDetails?.name}</h5>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="inline-block h-1 w-1 rounded-full" />
                      <span>{selectedCase?.category}</span>
                      <span
                        className={`rounded-full  text-xs font-medium ${priorityBadge(selectedCase.priority)}`}
                      >
                        {selectedCase.priority}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openChat(selectedCase)}
                    disabled={chatLoading}
                    title="Refresh Chat"
                    className="rounded-full p-2 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`h-5 w-5 text-blue-600 ${
                        chatLoading ? "animate-spin" : ""
                      }`}
                    />
                  </button>

                  <button
                    onClick={closeChat}
                    title="Close"
                    className="rounded-full p-2 transition hover:bg-gray-100"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 orange-scrollBar max-h-[500px] overflow-y-auto p-4">
                {chatLoading ? (
                  <div className="space-y-4 animate-pulse">
                    {[...Array(6)].map((_, index) => (
                      <div
                        key={index}
                        className={`flex ${index % 2 === 0 ? "justify-start" : "justify-end"}`}
                      >
                        <div
                          className={`rounded-2xl p-3 ${
                            index % 2 === 0 ? "bg-gray-200" : "bg-indigo-200"
                          }`}
                        >
                          <div className="mb-2 h-3 w-40 rounded bg-gray-300"></div>
                          <div className="h-3 w-24 rounded bg-gray-300"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : chatMessages.length === 0 ? (
                  <div className="flex h-full items-center justify-center py-10">
                    <p className="text-sm font-medium text-gray-500">
                      Chats Not Found!!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {chatMessages.map((msg) => {
                      const isCurrentUser = msg.authorModel === "Student";
                      return (
                        <div
                          key={msg._id}
                          className={`flex ${
                            isCurrentUser ? "justify-end" : "justify-start"
                          }`}
                        >
                          <div
                            className={`max-w-xs rounded-2xl px-4 py-2.5 shadow-sm lg:max-w-md ${
                              isCurrentUser
                                ? "rounded-br-none bg-indigo-600 text-white"
                                : "rounded-bl-none bg-gray-100 text-gray-900"
                            }`}
                          >
                            <p className="text-sm">{msg.comment}</p>

                            <div className="mt-1 flex items-center justify-between gap-2">
                              <span
                                className={`text-[10px] ${
                                  isCurrentUser
                                    ? "text-indigo-200"
                                    : "text-gray-500"
                                }`}
                              >
                                {new Date(msg.createdAt).toLocaleDateString(
                                  "en-GB",
                                )}{" "}
                                {new Date(msg.createdAt).toLocaleTimeString(
                                  "en-US",
                                  {
                                    hour: "numeric",
                                    minute: "2-digit",
                                    second: "2-digit",
                                    hour12: true,
                                  },
                                )}
                              </span>

                              <span
                                className={`rounded-full px-2 py-0.5 text-[9px] ${
                                  msg.visibility === "internal"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {msg.visibility}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Chat Input */}
              <div className="rounded-b-2xl border-t border-gray-200 p-4">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Type a message..."
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    className="flex-1 rounded-lg border-0 px-4 py-2.5 text-sm outline-none focus:ring-0"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sendingMessage || !chatMessage.trim()}
                    className="rounded-full bg-indigo-600 p-2.5 text-white disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
                {/* Media attachment note */}
                {/* <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Paperclip className="h-3 w-3" />
                    <span>Attach media – preview visible after posting</span>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        )}
        {isCaseDetailsModalOpen && selectedCase && (
          <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-lg">
            <div className="mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
              {/* Modal Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white p-4">
                <div>
                  <h3 className="text-lg font-semibold">Case Details</h3>
                  <p className="text-sm text-gray-500">
                    {selectedCase.caseNumber}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsCaseDetailsModalOpen(false);
                    setSelectedCase(null);
                  }}
                  className="rounded-full p-2 transition hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="space-y-6 p-6">
                {/* Status and Priority Badges */}
                <div className="flex flex-wrap gap-3">
                  <span
                    className={`rounded-full px-3 py-1 text-sm font-semibold ${
                      selectedCase.status === "new"
                        ? "bg-blue-100 text-blue-700"
                        : selectedCase.status === "assigned"
                          ? "bg-purple-100 text-purple-700"
                          : selectedCase.status === "in_progress"
                            ? "bg-yellow-100 text-yellow-700"
                            : selectedCase.status === "resolved"
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    Status:{" "}
                    {selectedCase.status?.replace("_", " ").toUpperCase() ||
                      "N/A"}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-sm font-semibold ${
                      selectedCase.priority === "critical"
                        ? "bg-red-100 text-red-700"
                        : selectedCase.priority === "high"
                          ? "bg-orange-100 text-orange-700"
                          : selectedCase.priority === "medium"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-green-100 text-green-700"
                    }`}
                  >
                    Priority: {selectedCase.priority?.toUpperCase() || "N/A"}
                  </span>
                  {selectedCase.isEmergency && (
                    <span className="rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-700">
                      🚨 Emergency
                    </span>
                  )}
                  {selectedCase.isAnonymous && (
                    <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700">
                      Anonymous Report
                    </span>
                  )}
                </div>

                {/* Basic Information */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-xs font-medium text-gray-500">
                      Case Number
                    </label>
                    <p className="text-sm font-medium">
                      {selectedCase.caseNumber}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">
                      Category
                    </label>
                    <p className="flex items-center gap-2 text-sm font-medium">
                      {selectedCase?.category}
                     
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">
                      Student
                    </label>
                    <p className="text-sm font-medium">
                      {studentDetails?.name || '-'
                        }
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">
                      Class
                    </label>
                    <p className="text-sm font-medium">
                      {studentDetails.standard?.name || '-'
                        }{" "}
                      -{" "}
                      {studentDetails.section?.name || '-'
                       }
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">
                      Reported On
                    </label>
                    <p className="text-sm font-medium">
                      {`${format(new Date(selectedCase.reportedOn), "dd/MM/yyyy")}, ${format(
                        new Date(selectedCase.reportedOn),
                        "h:mm:ss a",
                      )}`}
                    </p>
                  </div>
                  {selectedCase.closedAt && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        Closed On
                      </label>
                      <p className="text-sm font-medium">
                        {new Date(selectedCase.closedAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {selectedCase.commentCount !== undefined && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        Comments
                      </label>
                      <p className="text-sm font-medium">
                        {selectedCase.commentCount}
                      </p>
                    </div>
                  )}
                </div>

                {/* Description */}
                {selectedCase.description && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">
                      Description
                    </label>
                    <div className="mt-1 rounded-lg bg-gray-50 p-3">
                      <p className="text-sm">{selectedCase.description}</p>
                    </div>
                  </div>
                )}

                {/* Attachments */}
                {selectedCase.attachments &&
                  selectedCase.attachments.length > 0 && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        Attachments ({selectedCase.attachments.length})
                      </label>
                      <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-3">
                        {selectedCase.attachments.map((file, index) => {
                          const isImage =
                            /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|tiff|heic)$/i.test(
                              file,
                            );
                          const fileSize =
                            selectedCase.attachments_sizeKb?.[index] || 0;
                          const fileName =
                            file?.split("/").pop() || `Attachment ${index + 1}`;

                          // Build full URL - adjust base URL as needed
                          const baseUrl = process.env.REACT_APP_API_URL || "";
                          const fileUrl = file.startsWith("http")
                            ? file
                            : `${baseUrl}/${file}`;

                          return isImage ? (
                            <div key={index} className="group relative">
                              <div className="relative h-32 w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
                                <img
                                  src={fileUrl}
                                  alt={fileName}
                                  className="h-full w-full cursor-pointer object-cover transition-transform duration-200 group-hover:scale-105"
                                  onClick={() =>
                                    setPreviewImage && setPreviewImage(fileUrl)
                                  }
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    const parent = e.target.parentElement;
                                    if (parent) {
                                      const fallback =
                                        document.createElement("div");
                                      fallback.className =
                                        "flex items-center justify-center h-full bg-gray-100 text-gray-500 text-sm p-2";
                                      fallback.textContent = fileName;
                                      parent.appendChild(fallback);
                                    }
                                  }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
                                  <span className="rounded bg-white/90 px-2 py-1 text-xs opacity-0 transition-opacity group-hover:opacity-100">
                                    Click to Preview
                                  </span>
                                </div>
                              </div>
                              <div className="mt-1 flex items-center justify-between">
                                <span className="max-w-[120px] truncate text-xs text-gray-600">
                                  {fileName}
                                </span>
                                {fileSize > 0 && (
                                  <span className="text-xs text-gray-400">
                                    {(fileSize / 1024).toFixed(1)} MB
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <a
                              key={index}
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group flex h-32 flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-600 transition-colors hover:bg-gray-100"
                            >
                              <div className="flex flex-col items-center gap-1.5">
                                <Paperclip className="h-8 w-8 text-gray-400 transition-colors group-hover:text-gray-600" />
                                <span className="max-w-full truncate px-2 text-center text-xs">
                                  {fileName}
                                </span>
                                {fileSize > 0 && (
                                  <span className="text-xs text-gray-400">
                                    {(fileSize / 1024).toFixed(1)} MB
                                  </span>
                                )}
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {/* Assigned To */}
                {selectedCase.assignedTo && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">
                      Assigned To
                    </label>
                    <p className="text-sm font-medium">
                      {typeof selectedCase.assignedTo === "object"
                        ? selectedCase.assignedTo.name || "Unassigned"
                        : selectedCase.assignedTo || "Unassigned"}
                    </p>
                  </div>
                )}

                {/* Follow-up & Meeting Dates */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {selectedCase.followUpDate && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        Follow-up Date
                      </label>
                      <p className="text-sm font-medium">
                        {new Date(selectedCase.followUpDate).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {selectedCase.parentMeetingDate && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">
                        Parent Meeting Date
                      </label>
                      <p className="text-sm font-medium">
                        {new Date(
                          selectedCase.parentMeetingDate,
                        ).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Resolution Summary */}
                {selectedCase.resolutionSummary && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">
                      Resolution Summary
                    </label>
                    <div className="mt-1 rounded-lg bg-green-50 p-3">
                      <p className="text-sm">
                        {selectedCase.resolutionSummary}
                      </p>
                    </div>
                  </div>
                )}

                {/* Last Activity */}
                {selectedCase.lastActivityDate && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">
                      Last Activity
                    </label>
                    <p className="text-sm font-medium">
                      {new Date(selectedCase.lastActivityDate).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Footer with Action Buttons */}
              <div className="sticky bottom-0 flex justify-end gap-3 border-t border-gray-200 bg-white p-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCaseDetailsModalOpen(false);
                    setSelectedCase(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Safety;
